/**
 * The conversation list's logic and the transcript download: groups by last activity (Pinned,
 * Today, Yesterday, Previous 7 days, Previous 30 days, then by month), search over names and
 * the last words, and a conversation as Markdown. Pure, so it is tested (__tests__/listing.test.ts).
 */
import type { Conversation, ConversationSummary } from '@orgus/protocol';
import { formatMonth } from '@orgus/ui';
import { buildTurns } from './turns.ts';
import { describeSteps } from './turns.ts';

export interface ConversationGroup {
  key: string;
  label: string;
  items: ConversationSummary[];
}

/** At most this many pinned conversations. */
export const PIN_LIMIT = 5;

const DAY = 86_400_000;
const startOfDay = (t: number) => {
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

/** Groups, newest first; empty groups dropped. */
export function groupConversations(list: readonly ConversationSummary[], now: number): ConversationGroup[] {
  const today = startOfDay(now);
  const groups = new Map<string, ConversationGroup>();
  const put = (key: string, label: string, c: ConversationSummary) => {
    const g = groups.get(key) ?? { key, label, items: [] };
    g.items.push(c);
    groups.set(key, g);
  };
  const sorted = [...list].sort((a, b) => b.updatedAt - a.updatedAt);
  for (const c of sorted.filter((x) => x.pinned)) put('pinned', 'Pinned', c);
  for (const c of sorted.filter((x) => !x.pinned)) {
    const t = c.updatedAt;
    if (t >= today) put('today', 'Today', c);
    else if (t >= today - DAY) put('yesterday', 'Yesterday', c);
    else if (t >= today - 7 * DAY) put('week', 'Previous 7 days', c);
    else if (t >= today - 30 * DAY) put('month', 'Previous 30 days', c);
    else {
      const d = new Date(t);
      const key = `m${d.getFullYear()}-${d.getMonth()}`;
      put(key, d.getFullYear() === new Date(now).getFullYear() ? formatMonth(t) : `${formatMonth(t)} ${d.getFullYear()}`, c);
    }
  }
  return [...groups.values()];
}

/** Conversations whose name or last words hold every word searched for. */
export function searchConversations(list: readonly ConversationSummary[], query: string): ConversationSummary[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...list];
  return list.filter((c) => {
    const hay = `${c.title} ${c.lastText ?? ''}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** A conversation as Markdown: each message and answer with who and when, and what each reply did. */
export function transcriptMarkdown(c: Conversation, when: (ts: number) => string): string {
  const out: string[] = [`# ${c.title}`, '', `_${when(c.createdAt)}_`, ''];
  for (const t of buildTurns(c)) {
    if (t.user) out.push(`### You · ${when(t.user.at)}`, '', t.user.text ?? '', '');
    if (t.steps.length || t.answer) {
      out.push(`### Assistant · ${when(t.lastAt)}`, '');
      if (t.steps.length) out.push(`_${describeSteps(t.steps)}_`, '');
      if (t.answer) out.push(t.answer, '');
      for (const e of t.errors) out.push(`> ${e.text ?? ''}`, '');
    }
  }
  return `${out.join('\n').trimEnd()}\n`;
}

/** A file name for a transcript: assistant-2026-10-18-stop-every-idle-session.md. */
export function transcriptFileName(c: Pick<Conversation, 'title' | 'createdAt'>): string {
  const d = new Date(c.createdAt);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const slug = c.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 48)
    .replace(/-$/, '');
  return `assistant-${date}${slug ? `-${slug}` : ''}.md`;
}
